import React, { useEffect, useMemo, useRef, useState } from "react";
import { ClockIcon, DocumentDuplicateIcon, ExclamationTriangleIcon, PauseIcon } from "@heroicons/react/24/outline";
import { CloseIcon, PlayIcon } from "../reports/reportIcons";
import CronLogo from "./CronLogo";
import CronStatusPill from "./CronStatusPill";
import {
    describeCronSchedule,
    formatCronCompact,
    formatCronLongDate,
    formatCronRelative,
    formatCronTimeWithZone,
    getCronRuns,
    timeZoneLabel,
    validateCronExpression,
} from "./cronSchedule";

const SCHEDULE_PRESETS = [
    ["Every 15 min", "*/15 * * * *"],
    ["Hourly", "0 * * * *"],
    ["Daily 6 AM", "0 6 * * *"],
    ["Weekdays 8 AM", "0 8 * * 1-5"],
    ["Monthly, 1st", "0 11 1 * *"],
];

const secondaryButton = "inline-flex h-[30px] items-center gap-1.5 rounded-[7px] border border-[#ececef] bg-white px-2.5 text-[12.5px] font-medium text-[#0f1115] hover:bg-[#fafafb] disabled:cursor-not-allowed disabled:opacity-50";
const sectionTitle = "text-[11px] font-semibold uppercase tracking-[.1em] text-[#7b8090]";

const LongDate = ({ ts, now, empty }) => (ts == null ? (
    <span className="text-[#7b8090]">{empty}</span>
) : (
    <>
        {formatCronLongDate(ts)}
        <small className="block text-[11.5px] text-[#7b8090]">{formatCronTimeWithZone(ts)} · {formatCronRelative(ts, now)}</small>
    </>
));

const CronDetailsDrawer = ({
    row,
    now,
    canEdit,
    canRun,
    focusSchedule,
    isSaving,
    onClose,
    onRun,
    onTogglePaused,
    onSaveSchedule,
    onCopyType,
}) => {
    const [draft, setDraft] = useState(row?.cronInterval || "");
    const inputRef = useRef(null);

    useEffect(() => {
        setDraft(row?.cronInterval || "");
    }, [row?.key, row?.cronInterval]);

    useEffect(() => {
        if (focusSchedule && inputRef.current) inputRef.current.focus();
    }, [focusSchedule, row?.key]);

    const trimmedDraft = draft.trim();
    const dirty = trimmedDraft !== (row?.cronInterval || "");
    const error = dirty ? validateCronExpression(trimmedDraft) : null;
    const previewExpression = error ? null : trimmedDraft;
    const preview = useMemo(
        () => (previewExpression ? getCronRuns(previewExpression, row?.timeZone, { from: now, count: 5 }) : []),
        [previewExpression, row?.timeZone, now],
    );
    const previewLabel = previewExpression ? describeCronSchedule(previewExpression, row?.timeZone) : null;

    if (!row) return null;

    const editable = canEdit && !!row.cron?.id;
    const save = (event) => {
        event.preventDefault();
        if (!dirty || error || isSaving) return;
        onSaveSchedule(row, trimmedDraft);
    };

    return (
        <aside
            aria-label={`${row.name} details`}
            className="fixed inset-0 z-[110] w-full overflow-auto bg-white md:sticky md:top-0 md:z-auto md:h-screen md:w-[400px] md:flex-none md:border-l md:border-[#ececef] md:shadow-[-8px_0_24px_rgba(15,17,21,0.04)]"
        >
            <div className="px-5 pb-8 pt-[18px]">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-start gap-3">
                        <CronLogo type={row.type} size="lg" />
                        <div className="min-w-0">
                            <h2 className="text-[16px] font-[650] tracking-[-.015em] text-[#0f1115]">{row.name}</h2>
                            <p className="mt-[3px] truncate font-mono text-[11.5px] text-[#7b8090]">{row.type}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close details"
                        className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-[7px] text-[#7b8090] hover:bg-[#f3f3f5]"
                    >
                        <CloseIcon className="h-[13px] w-[13px]" />
                    </button>
                </div>

                <div className="mt-[14px] flex flex-wrap gap-2">
                    {canEdit && (
                        <button
                            type="button"
                            onClick={() => onRun(row)}
                            disabled={!canRun || row.isPaused || row.isRunning}
                            title={!canRun ? "This job has no manual run" : row.isPaused ? "Resume this job to run it" : undefined}
                            className="inline-flex h-[30px] items-center gap-1.5 rounded-[7px] bg-[#0f1115] px-2.5 text-[12.5px] font-medium text-white hover:bg-[#1c1f26] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <PlayIcon className="h-[12px] w-[12px]" />
                            {row.isRunning ? "Running…" : "Run now"}
                        </button>
                    )}
                    {editable && (
                        <button type="button" onClick={() => onTogglePaused(row)} className={secondaryButton}>
                            {row.isPaused ? <PlayIcon className="h-[12px] w-[12px] text-[#7b8090]" /> : <PauseIcon className="h-[14px] w-[14px] text-[#7b8090]" />}
                            {row.isPaused ? "Resume" : "Pause"}
                        </button>
                    )}
                    <button type="button" onClick={() => onCopyType(row)} className={secondaryButton}>
                        <DocumentDuplicateIcon className="h-[14px] w-[14px] text-[#7b8090]" />
                        Copy job type
                    </button>
                </div>

                <div className="mt-[18px] flex flex-wrap items-center gap-2">
                    <CronStatusPill status={row.status} />
                    {row.comingSoon && <span className="inline-flex h-[18px] items-center rounded bg-[#f3f3f5] px-1.5 text-[10.5px] font-medium text-[#7b8090]">Coming soon</span>}
                </div>
                {(row.message || row.missedAt) && (
                    <div className={`mt-2.5 flex gap-2 rounded-lg border px-3 py-2.5 text-[12.5px] leading-[1.45] ${
                        row.settledStatus === "ERROR" ? "border-[#fecaca] bg-[#fef2f2] text-[#991b1b]" : "border-[#fed7aa] bg-[#fff7ed] text-[#9a3412]"
                    }`}>
                        {row.message
                            ? <ExclamationTriangleIcon className="mt-px h-[15px] w-[15px] flex-none" />
                            : <ClockIcon className="mt-px h-[15px] w-[15px] flex-none" />}
                        <div className="min-w-0 break-words">
                            {row.message && <p>{row.isPaused ? `Last run before pause: ${row.message}` : row.message}</p>}
                            {row.missedAt && (
                                <p className={row.message ? "mt-1" : ""}>
                                    No run recorded for the {formatCronCompact(row.missedAt)} slot. Inferred from the schedule and the latest run.
                                </p>
                            )}
                        </div>
                    </div>
                )}

                <dl className="mt-[18px] grid grid-cols-[110px_1fr] gap-x-2.5 gap-y-[10px] text-[12.5px]">
                    <dt className="text-[#7b8090]">Category</dt>
                    <dd className="text-[#0f1115]">
                        <span className="inline-flex h-[22px] items-center rounded-md bg-[#f3f3f5] px-2 text-[12px] font-medium text-[#4b5060]">{row.category}</span>
                        <span className="ml-2 text-[#7b8090]">{row.kind === "report" ? "Report" : "Job"}</span>
                    </dd>
                    <dt className="text-[#7b8090]">Schedule</dt>
                    <dd className="text-[#0f1115]">
                        {row.schedule || (row.cronInterval ? "Custom schedule" : "No schedule")}
                        {row.cronInterval && <small className="block font-mono text-[11.5px] text-[#7b8090]">{row.cronInterval}</small>}
                    </dd>
                    <dt className="text-[#7b8090]">Time zone</dt>
                    <dd className="text-[#0f1115]">{row.timeZone} <span className="text-[#7b8090]">({timeZoneLabel(row.timeZone)})</span></dd>
                    <dt className="text-[#7b8090]">Last run</dt>
                    <dd className="text-[#0f1115]"><LongDate ts={row.lastRunAt} now={now} empty="No run recorded" /></dd>
                    <dt className="text-[#7b8090]">Next run</dt>
                    <dd className="text-[#0f1115]">
                        <LongDate ts={row.nextRunAt} now={now} empty={row.isPaused ? "Paused. Resume to schedule." : "Runs on demand"} />
                    </dd>
                    {row.cron?.description && (
                        <>
                            <dt className="text-[#7b8090]">Description</dt>
                            <dd className="text-[#4b5060]">{row.cron.description}</dd>
                        </>
                    )}
                </dl>

                <section className="mt-[22px]">
                    <h3 className={sectionTitle}>Schedule</h3>
                    <form onSubmit={save} className="mt-2.5">
                        <label htmlFor="cron-schedule-input" className="sr-only">Cron expression</label>
                        <input
                            id="cron-schedule-input"
                            ref={inputRef}
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            readOnly={!editable}
                            disabled={isSaving}
                            spellCheck={false}
                            aria-invalid={!!error}
                            aria-describedby="cron-schedule-help"
                            placeholder="minute hour day month weekday"
                            className={`h-9 w-full rounded-[7px] border bg-white px-3 font-mono text-[13px] text-[#0f1115] outline-none placeholder:font-sans placeholder:text-[#a2a6b3] read-only:bg-[#fafafb] read-only:text-[#4b5060] ${
                                error ? "border-[#fca5a5] focus:border-[#ef4444]" : "border-[#ececef] focus:border-[#c9cbd3]"
                            }`}
                        />
                        <p id="cron-schedule-help" className={`mt-1.5 text-[12px] ${error ? "text-[#b91c1c]" : "text-[#7b8090]"}`}>
                            {error || (previewExpression
                                ? previewLabel || `Custom schedule (${timeZoneLabel(row.timeZone)})`
                                : editable ? "Five fields: minute hour day-of-month month day-of-week." : "No schedule set.")}
                        </p>
                        {editable && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {SCHEDULE_PRESETS.map(([label, expression]) => (
                                    <button
                                        key={expression}
                                        type="button"
                                        onClick={() => setDraft(expression)}
                                        className={`h-6 rounded-md px-2 text-[11.5px] font-medium ${
                                            trimmedDraft === expression ? "bg-[#e6e6ea] text-[#0f1115]" : "bg-[#f3f3f5] text-[#4b5060] hover:text-[#0f1115]"
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        )}
                        {preview.length > 0 && (
                            <ol className="mt-3 space-y-1 text-[12px] text-[#4b5060]" aria-label="Next 5 runs">
                                {preview.map((ts) => (
                                    <li key={ts} className="flex justify-between gap-3 tabular-nums">
                                        <span>{formatCronCompact(ts)}</span>
                                        <span className="text-[#a2a6b3]">{formatCronRelative(ts, now)}</span>
                                    </li>
                                ))}
                            </ol>
                        )}
                        {editable && dirty && (
                            <div className="mt-3 flex justify-end gap-1.5">
                                <button type="button" onClick={() => setDraft(row.cronInterval || "")} disabled={isSaving} className="h-7 rounded-[7px] border border-[#ececef] bg-white px-2.5 text-[12px] text-[#0f1115] hover:bg-[#fafafb]">
                                    Cancel
                                </button>
                                <button type="submit" disabled={!!error || isSaving} className="h-7 rounded-[7px] bg-[#0f1115] px-2.5 text-[12px] text-white disabled:opacity-50">
                                    {isSaving ? "Saving…" : "Save schedule"}
                                </button>
                            </div>
                        )}
                        {canEdit && !row.cron?.id && (
                            <p className="mt-2 text-[12px] text-[#7b8090]">This job has no saved record yet, so its schedule can't be edited here.</p>
                        )}
                    </form>
                </section>

                <section className="mt-[22px]">
                    <h3 className={sectionTitle}>Latest run</h3>
                    {row.lastRunAt == null ? (
                        <p className="mt-2.5 text-[12.5px] text-[#7b8090]">No run recorded.</p>
                    ) : (
                        <div className="mt-2.5 flex items-center justify-between gap-3 rounded-lg border border-[#ececef] px-3 py-2.5 text-[12.5px]">
                            <span className="tabular-nums text-[#0f1115]">{formatCronCompact(row.lastRunAt)}</span>
                            <CronStatusPill status={row.isRunning ? row.settledStatus : row.status} />
                        </div>
                    )}
                    <p className="mt-2 text-[12px] text-[#a2a6b3]">Eadin stores only the latest run for each job. Full run history isn't recorded yet.</p>
                </section>
            </div>
        </aside>
    );
};

export default CronDetailsDrawer;
